import React, { useEffect, useState } from "react";
import { logInfo, logError } from "../utils/logger";
import Avatar from "@mui/material/Avatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useAuth } from "../providers/AuthProvider";
import { useTokenValidation } from "../providers/TokenValidationProvider";
import Paper from "@mui/material/Paper";
import DepTwitterConnectButton from "./DepTwitterConnectButton";
import TwitterConnectButtonSecure from "./TwitterConnectButtonSecure";
import Button from "@mui/material/Button";
import LinkedInConnectButton from "./LinkedInConnectButton";
import LinkedInConnectButtonSecure from "./LinkedInConnectButtonSecure";
import LinkedInOrgConnectButtonSecure from "./LinkedInOrgConnectButtonSecure";
import InstagramConnectButton from "./InstagramConnectButton";
import BlueSkyConnectButton from "./BlueSkyConnectButton";
import TikTokConnectButtonSecure from "./TikTokConnectButtonSecure";
import LinkedInOrgConnectButton from "./LinkedInOrgConnectButton";
import Box from "@mui/material/Box";
import ThreadsConnectButton from "./ThreadsConnectButton";
import MastodonConnectButton from "./MastodonConnectButton";
import FacebookConnectButtonSecure from "./FacebookConnectButtonSecure";
import { 
  Typography, 
  Chip, 
  Alert, 
  Tooltip,
  IconButton,
  CircularProgress
} from "@mui/material";
import { 
  Warning as WarningIcon, 
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon 
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const ExpiredAccountBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.error.main}`,
  backgroundColor: '#ffebee', // Light red background for accessibility
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#ffcdd2', // Slightly darker on hover
    boxShadow: theme.shadows[2],
  },
}));

const ValidAccountBox = styled(Box)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
  },
}));

const TokenStatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: '24px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    height: '20px',
  },
}));

export default function SocialAccountsDisplay({ filtered_account, onAccountRemoved }) {
    const auth = useAuth();
    const { 
      expiredAccounts, 
      isLoading, 
      forceValidation,
      checkValidationStatus 
    } = useTokenValidation();
    
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshingAccountId, setRefreshingAccountId] = useState(null);
    const [blockedAccountIds, setBlockedAccountIds] = useState(new Set());
    
    const totalConnectedAccounts = auth.user[0]?.userprofile.social_accounts.length || 0;
    const maxAllowedAccounts = auth.user[0]?.userprofile.subscription_plan?.max_social_accounts || 0;
    const hasReachedLimit = totalConnectedAccounts >= maxAllowedAccounts;

    const connectedAccounts = auth.user[0]?.userprofile.social_accounts.filter(
        account => account.platform === filtered_account
    );

    // Debug logging for expired accounts (development only)
    useEffect(() => {
        if (process.env.NODE_ENV === 'development' && connectedAccounts?.length > 0) {
            const expiredTwitterAccounts = connectedAccounts.filter(account => 
                account.platform === 'twitter' && isAccountExpired(account.id, account.platform)
            );
            
            console.log('SocialAccountsDisplay: Account status debug', {
                component: 'SocialAccountsDisplay',
                platform: filtered_account,
                connected_accounts: connectedAccounts.map(acc => ({
                    id: acc.id,
                    username: acc.username,
                    platform: acc.platform
                })),
                expired_accounts_from_provider: expiredAccounts?.map(exp => ({
                    account_id: exp.account_id,
                    platform: exp.platform,
                    error: exp.error_message
                })) || [],
                expired_twitter_count: expiredTwitterAccounts.length,
                is_account_expired_check: connectedAccounts.map(acc => ({
                    id: acc.id,
                    username: acc.username,
                    is_expired: isAccountExpired(acc.id, acc.platform)
                }))
            });
            
            if (expiredTwitterAccounts.length > 0) {
                logInfo('SocialAccountsDisplay: Expired Twitter accounts detected', {
                    component: 'SocialAccountsDisplay',
                    expired_count: expiredTwitterAccounts.length,
                    expired_accounts: expiredTwitterAccounts.map(acc => ({
                        id: acc.id,
                        username: acc.username
                    })),
                    total_expired_accounts: expiredAccounts?.length || 0
                });
            }
        }
    }, [connectedAccounts, expiredAccounts, filtered_account]);

    /**
     * Check if specific account is expired
     */
    const isAccountExpired = (accountId, platform) => {
      return expiredAccounts.some(expired => 
        expired.account_id === accountId && expired.platform === platform
      );
    };

    /**
     * Get expired account details for specific account
     */
    const getExpiredAccountDetails = (accountId, platform) => {
      return expiredAccounts.find(expired => 
        expired.account_id === accountId && expired.platform === platform
      );
    };

    /**
     * Force refresh token validation
     */
    const handleRefreshValidation = async () => {
      setIsRefreshing(true);
      try {
        await forceValidation();
      } catch (error) {
        console.error('Failed to refresh validation:', error);
      } finally {
        setIsRefreshing(false);
      }
    };

    /**
     * Clear refresh lock for Twitter account (for edge cases)
     */
    const handleClearRefreshLock = async (accountId) => {
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/twitter/clear-refresh-lock/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId,
                })
            });
            
            if (response.ok) {
                logInfo('SocialAccountsDisplay: Twitter refresh lock cleared', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId
                });
                
                // Remove from blocked accounts
                setBlockedAccountIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(accountId);
                    return newSet;
                });
                
                // Emit custom event to trigger token validation refresh
                window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                    detail: { platform: 'twitter', accountId: accountId, operation: 'lock_cleared' }
                }));
                
                // Automatically retry refresh after clearing lock
                await handleTwitterRefresh(accountId);
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Failed to clear refresh lock:', errorData.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Clear refresh lock error:', error);
        }
    };

    /**
     * Handle Twitter account refresh for expired tokens
     */
    const handleTwitterRefresh = async (accountId) => {
        setRefreshingAccountId(accountId);
        
        logInfo('SocialAccountsDisplay: Twitter refresh initiated', {
            component: 'SocialAccountsDisplay',
            account_id: accountId,
            user_id: auth.user[0]?.id
        });
        
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/refresh/twitter/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.redirect_url) {
                    logInfo('SocialAccountsDisplay: Redirecting to Twitter OAuth', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId,
                        redirect_domain: new URL(data.redirect_url).hostname
                    });
                    
                    // Redirect to Twitter OAuth for token refresh
                    window.location.href = data.redirect_url;
                } else {
                    logInfo('SocialAccountsDisplay: Twitter refresh completed without redirect', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId
                    });
                    
                    // Emit custom event to trigger token validation refresh
                    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                        detail: { platform: 'twitter', accountId: accountId }
                    }));
                    
                    // Refresh validation status if no redirect needed
                    await checkValidationStatus(true);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || 'Unknown error';
                
                logError('SocialAccountsDisplay: Twitter refresh failed', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId,
                    error: errorMessage,
                    status: response.status,
                    can_retry: errorData.can_retry,
                    retry_after: errorData.retry_after
                });
                
                // Handle specific error cases
                if (response.status === 409) {
                    // Conflict - refresh already in progress
                    const retryAfter = errorData.retry_after;
                    if (retryAfter && retryAfter > 30) {
                        // If more than 30 seconds left, mark as blocked and show clear button
                        setBlockedAccountIds(prev => new Set(prev).add(accountId));
                        console.error(`Twitter refresh blocked: ${errorMessage}. Retry in ${retryAfter} seconds.`);
                    } else {
                        console.error(`Twitter refresh blocked: ${errorMessage}`);
                    }
                } else if (errorData.can_retry) {
                    // Account mismatch - user can try again (lock already cleared by backend)
                    console.error(`Twitter refresh failed: ${errorMessage}`);
                } else {
                    console.error('Twitter refresh failed:', errorMessage);
                }
            }
        } catch (error) {
            logError('SocialAccountsDisplay: Twitter refresh error', {
                component: 'SocialAccountsDisplay',
                account_id: accountId,
                error: error.message
            });
            
            console.error('Twitter refresh error:', error);
        } finally {
            setRefreshingAccountId(null);
        }
    };

    /**
     * Handle Facebook account refresh for expired tokens
     */
    const handleFacebookRefresh = async (accountId) => {
        setRefreshingAccountId(accountId);
        
        logInfo('SocialAccountsDisplay: Facebook refresh initiated', {
            component: 'SocialAccountsDisplay',
            account_id: accountId,
            user_id: auth.user[0]?.id
        });
        
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/refresh/facebook/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.redirect_url) {
                    logInfo('SocialAccountsDisplay: Redirecting to Facebook OAuth', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId,
                        redirect_domain: new URL(data.redirect_url).hostname
                    });
                    
                    // Redirect to Facebook OAuth for token refresh
                    window.location.href = data.redirect_url;
                } else if (data.operation === 'refreshed') {
                    logInfo('SocialAccountsDisplay: Facebook refresh completed via token exchange', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId,
                        refreshed_accounts: data.refreshed_accounts?.length || 0
                    });
                    
                    // Show immediate success feedback
                    console.log(`✅ Facebook refresh successful: ${data.refreshed_accounts?.length || 0} accounts refreshed`);
                    
                    // Clear loading state immediately for successful refresh
                    setRefreshingAccountId(null);
                    
                    // Emit custom event to trigger token validation refresh
                    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                        detail: { platform: 'facebook', accountId: accountId, operation: 'refreshed' }
                    }));
                    
                    // Refresh validation status with timeout protection
                    try {
                        console.log('Starting checkValidationStatus for Facebook refresh...');
                        await Promise.race([
                            checkValidationStatus(true),
                            new Promise((_, reject) => 
                                setTimeout(() => reject(new Error('checkValidationStatus timeout')), 10000)
                            )
                        ]);
                        console.log('checkValidationStatus completed successfully');
                    } catch (error) {
                        console.warn('checkValidationStatus failed or timed out:', error);
                        // Continue anyway - don't let validation status checking block the UI
                    }
                } else {
                    logInfo('SocialAccountsDisplay: Facebook refresh completed without redirect', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId
                    });
                    
                    // Clear loading state immediately for successful refresh
                    setRefreshingAccountId(null);
                    
                    // Emit custom event to trigger token validation refresh
                    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                        detail: { platform: 'facebook', accountId: accountId }
                    }));
                    
                    // Refresh validation status if no redirect needed with timeout protection
                    try {
                        console.log('Starting checkValidationStatus for Facebook refresh (no redirect)...');
                        await Promise.race([
                            checkValidationStatus(true),
                            new Promise((_, reject) => 
                                setTimeout(() => reject(new Error('checkValidationStatus timeout')), 10000)
                            )
                        ]);
                        console.log('checkValidationStatus completed successfully (no redirect)');
                    } catch (error) {
                        console.warn('checkValidationStatus failed or timed out (no redirect):', error);
                        // Continue anyway - don't let validation status checking block the UI
                    }
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || 'Unknown error';
                
                logError('SocialAccountsDisplay: Facebook refresh failed', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId,
                    error: errorMessage,
                    status: response.status,
                    error_code: errorData.error_code
                });
                
                // Handle specific error cases
                if (response.status === 409) {
                    // Conflict - refresh already in progress
                    console.error(`Facebook refresh blocked: ${errorMessage}`);
                } else {
                    console.error('Facebook refresh failed:', errorMessage);
                }
            }
        } catch (error) {
            logError('SocialAccountsDisplay: Facebook refresh error', {
                component: 'SocialAccountsDisplay',
                account_id: accountId,
                error: error.message
            });
            
            console.error('Facebook refresh error:', error);
        } finally {
            setRefreshingAccountId(null);
        }
    };

    /**
     * Handle Instagram account refresh for expired tokens
     */
    const handleInstagramRefresh = async (accountId) => {
        setRefreshingAccountId(accountId);
        
        logInfo('SocialAccountsDisplay: Instagram refresh initiated', {
            component: 'SocialAccountsDisplay',
            account_id: accountId,
            user_id: auth.user[0]?.id
        });
        
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/refresh/instagram/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.redirect_url) {
                    logInfo('SocialAccountsDisplay: Redirecting to Instagram OAuth', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId,
                        redirect_domain: new URL(data.redirect_url).hostname
                    });
                    
                    // Redirect to Instagram OAuth for token refresh
                    window.location.href = data.redirect_url;
                } else {
                    logInfo('SocialAccountsDisplay: Instagram refresh completed without redirect', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId
                    });
                    
                    // Emit custom event to trigger token validation refresh
                    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                        detail: { platform: 'instagram', accountId: accountId }
                    }));
                    
                    // Refresh validation status if no redirect needed
                    await checkValidationStatus(true);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || 'Unknown error';
                
                logError('SocialAccountsDisplay: Instagram refresh failed', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId,
                    error: errorMessage,
                    status: response.status,
                    error_code: errorData.error_code
                });
                
                // Handle specific error cases
                if (response.status === 409) {
                    // Conflict - refresh already in progress
                    const retryAfter = errorData.retry_after;
                    if (retryAfter && retryAfter > 30) {
                        // If more than 30 seconds left, mark as blocked and show clear button
                        setBlockedAccountIds(prev => new Set(prev).add(accountId));
                        console.error(`Instagram refresh blocked: ${errorMessage}. Retry in ${retryAfter} seconds.`);
                    } else {
                        console.error(`Instagram refresh blocked: ${errorMessage}`);
                    }
                } else if (errorData.can_retry) {
                    // Account validation issue - user can try again
                    console.error(`Instagram refresh failed: ${errorMessage}`);
                } else {
                    console.error('Instagram refresh failed:', errorMessage);
                }
            }
        } catch (error) {
            logError('SocialAccountsDisplay: Instagram refresh error', {
                component: 'SocialAccountsDisplay',
                account_id: accountId,
                error: error.message
            });
            
            console.error('Instagram refresh error:', error);
        } finally {
            setRefreshingAccountId(null);
        }
    };

    /**
     * Handle LinkedIn account refresh for expired tokens
     */
    const handleLinkedInRefresh = async (accountId) => {
        setRefreshingAccountId(accountId);
        
        logInfo('SocialAccountsDisplay: LinkedIn refresh initiated', {
            component: 'SocialAccountsDisplay',
            account_id: accountId,
            user_id: auth.user[0]?.id
        });
        
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/refresh/linkedin/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                
                logInfo('SocialAccountsDisplay: LinkedIn refresh successful', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId,
                    refreshed_accounts: data.refreshed_accounts?.length || 0,
                    user_id: auth.user[0]?.id
                });
                
                // Emit custom event to trigger token validation refresh
                window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                    detail: { platform: 'linkedin', accountId: accountId }
                }));
                
                // Refresh validation status
                await checkValidationStatus(true);
                
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
                
                logError('SocialAccountsDisplay: LinkedIn refresh failed', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId,
                    error: errorMessage,
                    status_code: response.status,
                    error_code: errorData.error_code,
                    user_id: auth.user[0]?.id
                });
                
                // Handle specific error cases
                if (response.status === 409) {
                    // Conflict - refresh already in progress
                    const retryAfter = errorData.retry_after;
                    if (retryAfter && retryAfter > 30) {
                        // If more than 30 seconds left, mark as blocked
                        setBlockedAccountIds(prev => new Set(prev).add(accountId));
                        console.error(`LinkedIn refresh blocked: ${errorMessage}. Retry in ${retryAfter} seconds.`);
                    } else {
                        console.error(`LinkedIn refresh blocked: ${errorMessage}`);
                    }
                } else if (errorData.can_retry) {
                    // Account validation issue - user can try again
                    console.error(`LinkedIn refresh failed: ${errorMessage}`);
                } else {
                    console.error('LinkedIn refresh failed:', errorMessage);
                }
            }
        } catch (error) {
            logError('SocialAccountsDisplay: LinkedIn refresh error', {
                component: 'SocialAccountsDisplay',
                account_id: accountId,
                error: error.message,
                user_id: auth.user[0]?.id
            });
            
            console.error('LinkedIn refresh error:', error);
            
        } finally {
            setRefreshingAccountId(null);
        }
    };

    /**
     * Refresh BlueSky account tokens
     */
    const handleBlueSkyRefresh = async (accountId) => {
        console.log('BlueSky refresh attempt for account:', accountId);
        setRefreshingAccountId(accountId);
        
        try {
            const url = process.env.REACT_APP_API_BASE_URL + 'connect/refresh/bluesky/';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                
                if (data.redirect_url) {
                    // Handle redirect for re-authorization
                    console.log('BlueSky refresh requires re-authorization, redirecting...');
                    window.location.href = data.redirect_url;
                } else {
                    // Success - session refreshed
                    console.log('BlueSky refresh successful');
                    
                    // Emit custom event to trigger token validation refresh
                    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                        detail: { platform: 'bluesky', accountId: accountId }
                    }));
                    
                    // Refresh validation status to update UI
                    await checkValidationStatus(true);
                }
            } else {
                const errorData = await response.json();
                console.error('BlueSky refresh failed:', errorData);
                
                // Show error message to user
                window.dispatchEvent(new CustomEvent('tokenRefreshError', {
                    detail: { 
                        platform: 'bluesky', 
                        accountId: accountId,
                        error: errorData.error || 'Failed to refresh BlueSky connection'
                    }
                }));
            }
        } catch (error) {
            console.error('BlueSky refresh error:', error);
            
            // Show error message to user
            window.dispatchEvent(new CustomEvent('tokenRefreshError', {
                detail: { 
                    platform: 'bluesky', 
                    accountId: accountId,
                    error: 'Network error during BlueSky refresh'
                }
            }));
        } finally {
            setRefreshingAccountId(null);
        }
    };

    /**
     * Handle TikTok account refresh for expired tokens
     */
    const handleTikTokRefresh = async (accountId) => {
        setRefreshingAccountId(accountId);
        
        logInfo('SocialAccountsDisplay: TikTok refresh initiated', {
            component: 'SocialAccountsDisplay',
            account_id: accountId,
            user_id: auth.user[0]?.id
        });
        
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/refresh/tiktok/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    account_id: accountId
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.redirect_url) {
                    logInfo('SocialAccountsDisplay: Redirecting to TikTok OAuth', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId,
                        redirect_domain: new URL(data.redirect_url).hostname
                    });
                    
                    // Redirect to TikTok OAuth for token refresh
                    window.location.href = data.redirect_url;
                } else {
                    logInfo('SocialAccountsDisplay: TikTok refresh completed without redirect', {
                        component: 'SocialAccountsDisplay',
                        account_id: accountId
                    });
                    
                    // Emit custom event to trigger token validation refresh
                    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
                        detail: { platform: 'tiktok', accountId: accountId }
                    }));
                    
                    // Refresh validation status if no redirect needed
                    await checkValidationStatus(true);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || 'Unknown error';
                
                logError('SocialAccountsDisplay: TikTok refresh failed', {
                    component: 'SocialAccountsDisplay',
                    account_id: accountId,
                    error: errorMessage,
                    status: response.status,
                    error_code: errorData.error_code
                });
                
                // Handle specific error cases
                if (response.status === 409) {
                    // Conflict - refresh already in progress
                    const retryAfter = errorData.retry_after;
                    if (retryAfter && retryAfter > 30) {
                        // If more than 30 seconds left, mark as blocked
                        setBlockedAccountIds(prev => new Set(prev).add(accountId));
                        console.error(`TikTok refresh blocked: ${errorMessage}. Retry in ${retryAfter} seconds.`);
                    } else {
                        console.error(`TikTok refresh blocked: ${errorMessage}`);
                    }
                } else if (errorData.can_retry) {
                    // Account validation issue - user can try again
                    console.error(`TikTok refresh failed: ${errorMessage}`);
                } else {
                    console.error('TikTok refresh failed:', errorMessage);
                }
            }
        } catch (error) {
            logError('SocialAccountsDisplay: TikTok refresh error', {
                component: 'SocialAccountsDisplay',
                account_id: accountId,
                error: error.message
            });
            
            console.error('TikTok refresh error:', error);
        } finally {
            setRefreshingAccountId(null);
        }
    };

    /**
     * Handle account removal with validation refresh
     */
    const handleRemoveAccount = async (accountId) => {
        try {
            const url = process.env.REACT_APP_API_BASE_URL + `connect/remove/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${auth.token}`
                },
                body: JSON.stringify({
                    social_account_id: accountId,
                })
            });
            
            if (response.ok) {
                onAccountRemoved();
                
                // Emit custom event to trigger token validation refresh
                window.dispatchEvent(new CustomEvent('oauthComplete', {
                    detail: { platform: 'removal', operation: 'disconnected' }
                }));
                
                // Refresh validation status after account removal
                await checkValidationStatus(true);
            }
        } catch (error) {
            console.warn(error);
        }
    };

    /**
     * Get platform display name
     */
    const getPlatformDisplayName = (platform) => {
      const platformNames = {
        twitter: 'Twitter',
        facebook: 'Facebook',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        linkedin_org: 'LinkedIn Organization',
        tiktok: 'TikTok',
        threads: 'Threads',
        bluesky: 'BlueSky',
        mastodon: 'Mastodon',
      };
      return platformNames[platform] || platform;
    };

    const renderConnectButton = () => {
        if (hasReachedLimit) return null;

        switch(filtered_account) {
            case 'twitter':
                return <TwitterConnectButtonSecure />;
            case 'linkedin':
                return <LinkedInConnectButtonSecure />;
            case 'linkedin_org':
                return <LinkedInOrgConnectButtonSecure />;
            case 'instagram':
                return <InstagramConnectButton />;
            case 'threads':
                return <ThreadsConnectButton />;
            case 'bluesky':
                return <BlueSkyConnectButton />;
            case 'tiktok':
                return <TikTokConnectButtonSecure />;
            case 'mastodon':
                return <MastodonConnectButton />;
            case 'facebook':
                return <FacebookConnectButtonSecure />;
            default:
                return null;
        }
    };

    const renderLimitMessage = () => {
        if (hasReachedLimit) {
            return (
                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: 'warning.light',
                        color: 'warning.dark'
                    }}
                >
                    You've reached the maximum number of social accounts ({maxAllowedAccounts})
                    allowed in your subscription plan. To connect more accounts, please either
                    disconnect an existing account or upgrade your plan.
                </Paper>
            );
        }
        return null;
    };

    /**
     * Render token status indicator
     */
    const renderTokenStatus = (account) => {
      const expired = isAccountExpired(account.id, account.platform);
      const expiredDetails = getExpiredAccountDetails(account.id, account.platform);
      
      if (expired) {
        return (
          <Tooltip 
            title={expiredDetails?.error_message || "Authentication expired - reconnection required"}
            arrow
          >
            <TokenStatusChip
              icon={<WarningIcon sx={{ fontSize: '16px !important' }} />}
              label="Expired"
              color="error"
              variant="filled"
              size="small"
            />
          </Tooltip>
        );
      }
      
      return (
        <Tooltip title="Account connected and working" arrow>
          <TokenStatusChip
            icon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
            label="Active"
            color="success"
            variant="outlined"
            size="small"
          />
        </Tooltip>
      );
    };

    /**
     * Render refresh button for platform section
     */
    const renderRefreshButton = () => {
      if (connectedAccounts.length === 0) return null;
      
      return (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Tooltip title="Refresh token status" arrow>
            <IconButton
              onClick={handleRefreshValidation}
              disabled={isRefreshing || isLoading}
              size="small"
              aria-label="Refresh token validation status"
            >
              {isRefreshing || isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      );
    };

    if (!connectedAccounts?.length) {
        return (
            <Box>
                <Paper
                    elevation={2}
                    sx={{ p: 2, mb: 2 }}
                    variant="outlined"
                >
                    You haven't connected to {getPlatformDisplayName(filtered_account)} yet.
                    {!hasReachedLimit && " Click the button below to connect your account."}
                </Paper>
                {renderLimitMessage()}
                {renderConnectButton()}
            </Box>
        );
    }

    return (
        <Box>
            {renderLimitMessage()}
            {renderRefreshButton()}
            
            {connectedAccounts.map(account => {
              const expired = isAccountExpired(account.id, account.platform);
              const expiredDetails = getExpiredAccountDetails(account.id, account.platform);
              const AccountContainer = expired ? ExpiredAccountBox : ValidAccountBox;
              
              return (
                <AccountContainer
                    key={account.user_id}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1, sm: 2 },
                        mb: 2,
                        p: { xs: 1.5, sm: 2 },
                        justifyContent: 'space-between'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: { xs: 1, sm: 0 },
                            flex: 1
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <Avatar
                                alt={account.username}
                                src={account.account_img}
                                sx={{ 
                                  width: { xs: 32, sm: 40 }, 
                                  height: { xs: 32, sm: 40 },
                                  ...(expired && {
                                    border: '2px solid',
                                    borderColor: 'error.main'
                                  })
                                }}
                            />
                        </ListItemIcon>
                        
                        <Box sx={{ 
                            flexGrow: 1,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            wordBreak: 'break-word'
                        }}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body1" fontWeight="bold">
                              {account.username}
                            </Typography>
                            {renderTokenStatus(account)}
                          </Box>
                          
                          {expired && expiredDetails && (
                            <Typography 
                              variant="caption" 
                              color="error.main"
                              sx={{ 
                                display: 'block',
                                mt: 0.5,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {expiredDetails.error_message || 'Reconnection required'}
                            </Typography>
                          )}
                        </Box>
                    </Box>

                    <Box 
                      display="flex" 
                      flexDirection={{ xs: 'row', sm: 'column' }} 
                      gap={1}
                      alignItems="center"
                    >
                      {expired && (
                        <Alert 
                          severity="error" 
                          variant="outlined"
                          sx={{ 
                            p: 0.5,
                            minWidth: 0,
                            fontSize: '0.75rem',
                            '& .MuiAlert-message': {
                              fontSize: 'inherit',
                              p: 0,
                            },
                            '& .MuiAlert-icon': {
                              fontSize: '1rem',
                              mr: 0.5,
                            }
                          }}
                        >
                          Needs reconnection
                        </Alert>
                      )}
                      
                      {/* Show refresh button for expired accounts */}
                      {expired && (account.platform === 'twitter' || account.platform === 'facebook' || account.platform === 'facebook_personal' || account.platform === 'instagram' || account.platform === 'linkedin' || account.platform === 'linkedin_org' || account.platform === 'bluesky' || account.platform === 'tiktok') && (
                        <>
                          {/* Twitter-specific refresh with clear & retry option */}
                          {account.platform === 'twitter' && blockedAccountIds.has(account.id) ? (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleClearRefreshLock(account.id)}
                              startIcon={<RefreshIcon />}
                              color="info"
                              sx={{
                                alignSelf: { xs: 'stretch', sm: 'center' },
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Clear & Retry
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                if (account.platform === 'twitter') {
                                  handleTwitterRefresh(account.id);
                                } else if (account.platform === 'facebook' || account.platform === 'facebook_personal') {
                                  handleFacebookRefresh(account.id);
                                } else if (account.platform === 'instagram') {
                                  handleInstagramRefresh(account.id);
                                } else if (account.platform === 'linkedin' || account.platform === 'linkedin_org') {
                                  handleLinkedInRefresh(account.id);
                                } else if (account.platform === 'bluesky') {
                                  handleBlueSkyRefresh(account.id);
                                } else if (account.platform === 'tiktok') {
                                  handleTikTokRefresh(account.id);
                                }
                              }}
                              disabled={refreshingAccountId === account.id}
                              startIcon={
                                refreshingAccountId === account.id ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <RefreshIcon />
                                )
                              }
                              color="warning"
                              sx={{
                                alignSelf: { xs: 'stretch', sm: 'center' },
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold'
                              }}
                            >
                              {refreshingAccountId === account.id ? 'Refreshing...' : 'Refresh'}
                            </Button>
                          )}
                        </>
                      )}
                      
                      <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleRemoveAccount(account.id)}
                          color="error"
                          sx={{
                              alignSelf: { xs: 'stretch', sm: 'center' },
                              mt: { xs: 1, sm: 0 },
                              whiteSpace: 'nowrap'
                          }}
                      >
                          Disconnect
                      </Button>
                    </Box>
                </AccountContainer>
              );
            })}
            {renderConnectButton()}
        </Box>
    );
}